import { Close } from '@mui/icons-material';
import {
  Avatar,
  AvatarGroup,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import AssetBadge from '@/components/ui/AssetBadge';
import { CoinIcon } from '@/components/wallets';

import { formatCurrency } from '@bitstopco/bitstop-theme/helpers';

import type { TransactionsStats, WalletCoin } from '@/types';
import type { DispenseAssetView } from '@/types/generated/athena';

type Props = {
  controlled: boolean;
  allCoins: WalletCoin[];
  allAssets: DispenseAssetView[];
  selectedCoinStats: TransactionsStats['coins'];
  selectedAssetStats: TransactionsStats['assets'];
  totalFees: string;
  totalTransactions: string;
  totalSales: string;
  selectedCoins: WalletCoin[];
  selectedAssets: DispenseAssetView[];
  onSelectCoin: (coin: WalletCoin) => any;
  onSelectAsset: (asset: DispenseAssetView) => any;
  onClose: () => any;
};

const StatLine = ({
  total,
  coin,
  asset,
}: { total: string; coin?: WalletCoin; asset?: DispenseAssetView }) => (
  <Stack direction="row" alignItems="baseline" gap={1}>
    <Stack direction="row" alignItems="center" gap={1}>
      <CoinIcon coin={coin} asset={asset} width={20} height={20} hideChain />
      <Typography variant="body2">{coin?.chain.name ?? asset?.name}</Typography>
      <Typography variant="body2">/</Typography>
      <Typography component="span" variant="body2" fontWeight="bold">
        {coin?.ticker ?? asset?.symbol}
      </Typography>
    </Stack>
    <Box flex={1} borderBottom="1px dashed" borderColor="text.disabled" />
    <Typography variant="body2">{total}</Typography>
  </Stack>
);

const TransactionsCardStatsModal = ({
  selectedCoinStats,
  selectedAssetStats,
  allCoins,
  allAssets,
  selectedCoins,
  selectedAssets,
  totalTransactions,
  totalSales,
  totalFees,
  onSelectCoin,
  onSelectAsset,
  onClose,
  controlled,
}: Props) => {
  return (
    <Dialog data-test="stats-modal" open={true} fullWidth maxWidth="md" onClose={onClose}>
      <DialogTitle display="flex" flexDirection="row" justifyContent="space-between" width="100%">
        Transaction Totals
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack gap={3} py={2}>
          {controlled && (
            <Stack gap={1}>
              <Typography variant="h5">My Assets</Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {allCoins.map((coin) => (
                  <AssetBadge
                    selected={!!selectedCoins.find(({ id }) => coin.id === id)}
                    onClick={() => onSelectCoin(coin)}
                    key={coin.id}
                    coin={coin}
                  />
                ))}
                {allAssets.map((asset) => (
                  <AssetBadge
                    selected={!!selectedAssets.find(({ id }) => asset.id === id)}
                    onClick={() => onSelectAsset(asset)}
                    key={asset.id}
                    asset={asset}
                  />
                ))}
              </Stack>
            </Stack>
          )}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography color="text.secondary" variant="body2">
              Selected assets ({selectedCoins.length + selectedAssets.length})
            </Typography>
            <AvatarGroup max={999}>
              {selectedCoins.map((coin) => (
                <Avatar sx={{ width: 24, height: 24 }} key={coin.id}>
                  <CoinIcon width={24} height={24} coin={coin} hideChain />
                </Avatar>
              ))}
              {selectedAssets.map((asset) => (
                <Avatar sx={{ width: 24, height: 24 }} key={asset.id}>
                  <CoinIcon width={24} height={24} asset={asset} hideChain />
                </Avatar>
              ))}
            </AvatarGroup>
          </Stack>
          <Stack gap={3}>
            <Stack gap={1}>
              <Stack direction="row" alignItems="baseline" gap={1}>
                <Typography variant="body2" fontWeight="bold">
                  Total Sales
                </Typography>
                <Box flex={1} borderBottom="1px dashed" borderColor="text.disabled" />
                <Typography variant="body2" fontWeight="bold">
                  {totalSales}
                </Typography>
              </Stack>
              <Stack gap={1} borderLeft={3} borderColor="error.main" pl={2}>
                {selectedCoinStats.map(({ coin, total_sales }) => (
                  <StatLine key={coin.id} coin={coin} total={formatCurrency(total_sales)} />
                ))}
                {selectedAssetStats.map(({ asset, total_sales }) => (
                  <StatLine key={asset.id} asset={asset} total={formatCurrency(total_sales)} />
                ))}
              </Stack>
            </Stack>
            <Stack gap={1}>
              <Stack direction="row" alignItems="baseline" gap={1}>
                <Typography variant="body2" fontWeight="bold">
                  Total Fees
                </Typography>
                <Box flex={1} borderBottom="1px dashed" borderColor="text.disabled" />
                <Typography variant="body2" fontWeight="bold">
                  {totalFees}
                </Typography>
              </Stack>
              <Stack gap={1} borderLeft={3} borderColor="error.main" pl={2}>
                {selectedCoinStats.map(({ coin, total_fees }) => (
                  <StatLine key={coin.id} coin={coin} total={formatCurrency(total_fees)} />
                ))}
                {selectedAssetStats.map(({ asset, total_fees }) => (
                  <StatLine key={asset.id} asset={asset} total={formatCurrency(total_fees)} />
                ))}
              </Stack>
            </Stack>
            <Stack gap={1}>
              <Stack direction="row" alignItems="baseline" gap={1}>
                <Typography variant="body2" fontWeight="bold">
                  Total Transactions
                </Typography>
                <Box flex={1} borderBottom="1px dashed" borderColor="text.disabled" />
                <Typography variant="body2" fontWeight="bold">
                  {totalTransactions}
                </Typography>
              </Stack>
              <Stack gap={1} borderLeft={3} borderColor="error.main" pl={2}>
                {selectedCoinStats.map(({ coin, total_transactions }) => (
                  <StatLine key={coin.id} coin={coin} total={String(total_transactions)} />
                ))}
                {selectedAssetStats.map(({ asset, total_transactions }) => (
                  <StatLine key={asset.id} asset={asset} total={String(total_transactions)} />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TransactionsCardStatsModal;
